import { useEffect, useRef, useState, type ReactNode } from 'react';
import { errorMessage } from '@/lib/api';
import { Button } from './index';

/**
 * t449u: the one footer for settings and edit dialogs. Cancel (secondary) closes and throws the edits away; Save
 * (primary) saves everything and the dialog closes. Esc, the ✕ and a click outside do what Cancel does, because the
 * Modal's onClose is the same discard. A failed save shows its error here and the dialog stays open.
 * `start` holds a quieter action on the left (Remove, Archive).
 */
export function DialogFooter({ onCancel, onSave, saveLabel = 'Save', cancelLabel = 'Cancel', saving, disabled, error, start, saveTestId }: {
  onCancel: () => void; onSave: () => void; saveLabel?: ReactNode; cancelLabel?: string; saving?: boolean; disabled?: boolean;
  error?: string | null; start?: ReactNode; saveTestId?: string;
}) {
  return (
    <div className="flex w-full flex-wrap items-center justify-end gap-2" data-testid="dialog-footer">
      {start && <div className="mr-auto flex items-center gap-2">{start}</div>}
      {error && <p role="alert" className="mr-auto min-w-0 flex-1 text-sm text-rose-700 dark:text-rose-300" data-testid="dialog-error">{error}</p>}
      <Button variant="secondary" onClick={onCancel} disabled={saving} data-testid="dialog-cancel">{cancelLabel}</Button>
      <Button onClick={onSave} loading={saving} disabled={disabled} data-testid={saveTestId ?? 'dialog-save'}>{saveLabel}</Button>
    </div>
  );
}

/**
 * Save-and-close for a dialog: `run(fn)` saves, then calls onClose. If fn throws (or returns false) the dialog stays
 * open with the error for DialogFooter. Use the same onClose for Cancel, Esc and the ✕.
 */
export function useDialogSave(onClose: () => void) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const alive = useRef(true);
  useEffect(() => () => { alive.current = false; }, []);
  const close = useRef(onClose);
  close.current = onClose;
  const run = async (fn: () => Promise<unknown>): Promise<boolean> => {
    setSaving(true);
    setError(null);
    try {
      if (await fn() === false) return false;
      close.current();
      return true;
    } catch (e) {
      if (alive.current) setError(errorMessage(e));
      return false;
    } finally {
      if (alive.current) setSaving(false);
    }
  };
  return { saving, error, setError, run };
}
